/**
 * Purchase Order Model - Processes API responses for purchase order data
 */

/**
 * Process purchase order list response data
 * @param {Object} response - API response from purchase order list endpoint
 * @returns {Object} Formatted purchase order list data
 */
export const getPurchaseOrders = (response) => {
  if (response && response.data && response.data.success === true) {
    const purchaseOrders = Array.isArray(response.data.data)
      ? response.data.data.map((po) => ({
          id: po.id || null,
          slack: po.slack || '',
          store_id: po.store_id || null,
          po_number: po.po_number || '',
          po_reference: po.po_reference || null,
          order_date: po.order_date || '',
          order_due_date: po.order_due_date || '',
          supplier_id: po.supplier_id || null,
          supplier_code: po.supplier_code || '',
          supplier_name: po.supplier_name || '',
          supplier_address: po.supplier_address || '',
          currency_name: po.currency_name || '',
          currency_code: po.currency_code || '',
          tax_option_id: po.tax_option_id || null,
          subtotal_excluding_tax: po.subtotal_excluding_tax || '0',
          total_discount_amount: po.total_discount_amount || '0',
          total_after_discount: po.total_after_discount || '0',
          total_tax_amount: po.total_tax_amount || '0',
          shipping_charge: po.shipping_charge || '0',
          packing_charge: po.packing_charge || '0',
          total_order_amount: po.total_order_amount || '0',
          total_order_amount_actual: po.total_order_amount_actual || '0',
          terms: po.terms || null,
          update_stock: po.update_stock ?? 0,
          status: po.status ?? 0,
          file_key: po.file_key || null,
          file_name: po.file_name || null,
          place_holder1: po.place_holder1 || null,
          place_holder2: po.place_holder2 || null,
          place_holder3: po.place_holder3 || null,
          place_holder4: po.place_holder4 || null,
          place_holder5: po.place_holder5 || null,
          created_at: po.created_at || '',
          updated_at: po.updated_at || '',
          createdAt: po.createdAt || po.created_at || '',
          updatedAt: po.updatedAt || po.updated_at || '',
          created_by: po.created_by,
          updated_by: po.updated_by,
          created_user: po.created_user ? {
            id: po.created_user.id || null,
            fullname: po.created_user.fullname || ''
          } : null,
          updated_user: po.updated_user ? {
            id: po.updated_user.id || null,
            fullname: po.updated_user.fullname || ''
          } : null,
          created_by_user: po.created_by && typeof po.created_by === 'object' ? {
            id: po.created_by.id || null,
            username: po.created_by.username || '',
            email: po.created_by.email || ''
          } : null,
          updated_by_user: po.updated_by && typeof po.updated_by === 'object' ? {
            id: po.updated_by.id || null,
            username: po.updated_by.username || '',
            email: po.updated_by.email || ''
          } : null,
          status_data: po.status_data ? {
            id: po.status_data.id || null,
            key: po.status_data.key || '',
            value: po.status_data.value || '',
            value_constant: po.status_data.value_constant || '',
            label: po.status_data.label || '',
            color: po.status_data.color || '',
            status: po.status_data.status ?? 0
          } : null,
          store: po.store ? {
            id: po.store.id || null,
            slack: po.store.slack || '',
            store_code: po.store.store_code || '',
            name: po.store.name || '',
            tax_number: po.store.tax_number || '',
            address: po.store.address || '',
            pincode: po.store.pincode || '',
            primary_contact: po.store.primary_contact || '',
            secondary_contact: po.store.secondary_contact || null,
            primary_email: po.store.primary_email || '',
            secondary_email: po.store.secondary_email || null,
            country: po.store.country ? {
              id: po.store.country.id || null,
              name: po.store.country.name || '',
              code: po.store.country.code || ''
            } : null,
            status: po.store.status ?? 0,
            status_data: po.store.status_data ? {
              id: po.store.status_data.id || null,
              key: po.store.status_data.key || '',
              value: po.store.status_data.value || '',
              value_constant: po.store.status_data.value_constant || '',
              label: po.store.status_data.label || '',
              color: po.store.status_data.color || ''
            } : null,
            invoice_type: po.store.invoice_type || '',
            invoice_format: po.store.invoice_format || '',
            restaurant_mode: po.store.restaurant_mode ?? 0,
            digital_menu_enabled: po.store.digital_menu_enabled ?? 0,
            is_primary: po.store.is_primary ?? false,
            store_type: po.store.store_type || '',
            is_procurement_hub: po.store.is_procurement_hub ?? 0,
            can_sell: po.store.can_sell ?? false,
            org_id: po.store.org_id || null
          } : null,
          supplier: po.supplier ? {
            id: po.supplier.id || null,
            slack: po.supplier.slack || '',
            supplier_code: po.supplier.supplier_code || '',
            name: po.supplier.name || '',
            display_name: po.supplier.display_name || null,
            supplier_type: po.supplier.supplier_type || '',
            business_type: po.supplier.business_type || null,
            website: po.supplier.website || null,
            email: po.supplier.email || null,
            phone: po.supplier.phone || '',
            alternate_phone: po.supplier.alternate_phone || null,
            primary_contact_name: po.supplier.primary_contact_name || null,
            primary_contact_designation: po.supplier.primary_contact_designation || null,
            address: po.supplier.address || '',
            billing_address: po.supplier.billing_address || null,
            shipping_address: po.supplier.shipping_address || null,
            country_id: po.supplier.country_id || null,
            state_code: po.supplier.state_code || '',
            state: po.supplier.state || '',
            city: po.supplier.city || null,
            district: po.supplier.district || null,
            pincode: po.supplier.pincode || '',
            currency_code: po.supplier.currency_code || '',
            payment_terms: po.supplier.payment_terms || null,
            payment_term_days: po.supplier.payment_term_days ?? 0,
            delivery_terms: po.supplier.delivery_terms || null,
            incoterm_code: po.supplier.incoterm_code || null,
            preferred_supplier: po.supplier.preferred_supplier ?? 0,
            gst_registered: po.supplier.gst_registered ?? 0,
            gstin: po.supplier.gstin || null,
            pan_number: po.supplier.pan_number || null,
            maximum_credit_limit: po.supplier.maximum_credit_limit || null,
            bank_name: po.supplier.bank_name || null,
            bank_branch: po.supplier.bank_branch || null,
            account_holder_name: po.supplier.account_holder_name || null,
            account_number: po.supplier.account_number || null,
            ifsc_code: po.supplier.ifsc_code || null,
            swift_code: po.supplier.swift_code || null,
            upi_id: po.supplier.upi_id || null,
            status: po.supplier.status ?? 0,
            notification_enabled: po.supplier.notification_enabled ?? 0,
            notification_medium: po.supplier.notification_medium || '',
            notification_email: po.supplier.notification_email || null,
            notification_phone: po.supplier.notification_phone || null,
            internal_notes: po.supplier.internal_notes || null,
            status_data: po.supplier.status_data || null
          } : null,
          tax_option: po.tax_option ? {
            id: po.tax_option.id || null,
            tax_option_constant: po.tax_option.tax_option_constant || '',
            label: po.tax_option.label || '',
            description: po.tax_option.description || '',
            status: po.tax_option.status ?? 0
          } : null,
          products: Array.isArray(po.products) ? po.products.map((p) => ({
            id: p.id || null,
            slack: p.slack || '',
            purchase_order_id: p.purchase_order_id || null,
            product_id: p.product_id || null,
            product_slack: p.product_slack || '',
            product_code: p.product_code || '',
            name: p.name || '',
            quantity: p.quantity || '0',
            amount_excluding_tax: p.amount_excluding_tax || '0',
            discount_percentage: p.discount_percentage || '0',
            tax_type: p.tax_type || '',
            tax_percentage: p.tax_percentage || '0',
            discount_amount: p.discount_amount || '0',
            total_after_discount: p.total_after_discount || '0',
            tax_amount: p.tax_amount || '0',
            tax_components: Array.isArray(p.tax_components) ? p.tax_components : [],
            total_amount: p.total_amount || '0',
            received_quantity: p.received_quantity ?? 0,
            usage_type: p.usage_type || null,
            inventory_nature: p.inventory_nature || null,
            consumption_nature: p.consumption_nature || null,
            expiry_tracking: p.expiry_tracking || 'NO',
            batch_tracking: p.batch_tracking || 'NO',
            unit_of_measure: p.unit_of_measure || '',
            product_images: Array.isArray(p.product_images) ? p.product_images : [],
            status: p.status ?? 0,
            place_holder1: p.place_holder1 || null,
            place_holder2: p.place_holder2 || null,
            place_holder3: p.place_holder3 || null,
            place_holder4: p.place_holder4 || null,
            place_holder5: p.place_holder5 || null,
            meta_data: Array.isArray(p.meta_data) ? p.meta_data : []
          })) : [],
          grns: Array.isArray(po.grns) ? po.grns.map((g) => ({
            id: g.id || null,
            slack: g.slack || '',
            grn_number: g.grn_number || '',
            grn_date: g.grn_date || '',
            purchase_order_id: g.purchase_order_id || null,
            po_slack: g.po_slack || '',
            po_number: g.po_number || '',
            supplier_name: g.supplier_name || '',
            invoice_number: g.invoice_number || '',
            invoice_date: g.invoice_date || '',
            total_ordered_quantity: g.total_ordered_quantity || '0',
            total_received_quantity: g.total_received_quantity || '0',
            total_accepted_quantity: g.total_accepted_quantity || '0',
            total_rejected_quantity: g.total_rejected_quantity || '0',
            quality_check_status: g.quality_check_status || 'PENDING',
            inventory_updated: g.inventory_updated ?? 0,
            status: g.status ?? 0,
            status_data: g.status_data || null,
            store: g.store || null,
            supplier: g.supplier || null,
            products: Array.isArray(g.products) ? g.products.map((gp) => ({
              id: gp.id || null,
              slack: gp.slack || '',
              product_id: gp.product_id || null,
              product_code: gp.product_code || '',
              product_name: gp.product_name || '',
              quantity: gp.quantity || '0',
              received_quantity: gp.received_quantity || '0',
              accepted_quantity: gp.accepted_quantity || '0',
              rejected_quantity: gp.rejected_quantity || '0',
              status: gp.status ?? 0
            })) : []
          })) : [],
          invoices: Array.isArray(po.invoices) ? po.invoices : [],
          linked_documents: po.linked_documents || null,
          ship_to: po.ship_to ? {
            name: po.ship_to.name || '',
            address: po.ship_to.address || '',
            pincode: po.ship_to.pincode || '',
            tax_number: po.ship_to.tax_number || '',
            primary_email: po.ship_to.primary_email || '',
            secondary_email: po.ship_to.secondary_email || '',
            primary_contact: po.ship_to.primary_contact || '',
            secondary_contact: po.ship_to.secondary_contact || ''
          } : null,
          related_entities: po.related_entities || null
        }))
      : []

    return {
      success: true,
      total: response.data.total || purchaseOrders.length,
      data: purchaseOrders
    }
  }
  return {
    success: false,
    message: response?.data?.message || 'Failed to fetch purchase order list',
    data: []
  }
}

/**
 * Process single purchase order details response
 * @param {Object} response - API response from purchase order details endpoint
 * @returns {Object} Formatted purchase order details
 */
export const getPurchaseOrder = (response) => {
  if (response && response.data && response.data.success === true) {
    const item = response.data.data || {}
    return {
      success: true,
      data: item
    }
  }
  return {
    success: false,
    message:
      response?.data?.message || 'Failed to fetch purchase order details',
    data: {}
  }
}

/**
 * Process purchase order creation response
 * @param {Object} response - API response from purchase order create endpoint
 * @returns {Object} Formatted creation response
 */
export const createPurchaseOrder = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message: response.data.message || 'Purchase order created successfully',
      data: response.data.data || null
    }
  }
  return {
    success: false,
    message: 'Failed to create purchase order',
    data: null
  }
}

/**
 * Process purchase order update response
 * @param {Object} response - API response from purchase order update endpoint
 * @returns {Object} Formatted update response
 */
export const updatePurchaseOrder = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message: response.data.message || 'Purchase order updated successfully',
      data: response.data.data || null
    }
  }
  return {
    success: false,
    message: 'Failed to update purchase order',
    data: null
  }
}

/**
 * Process purchase order status update response
 * @param {Object} response - API response from purchase order status update endpoint
 * @returns {Object} Formatted status update response
 */
export const updatePurchaseOrderStatus = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message:
        response.data.message || 'Purchase order status updated successfully',
      data: response.data.data || null
    }
  }
  return {
    success: false,
    message: 'Failed to update status',
    data: null
  }
}

/**
 * Process purchase order delete response
 * @param {Object} response - API response from purchase order delete endpoint
 * @returns {Object} Formatted delete response
 */
export const deletePurchaseOrder = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message: response.data.message || 'Purchase order deleted successfully'
    }
  }
  return {
    success: false,
    message: 'Failed to delete purchase order'
  }
}

/**
 * Process invoice generation from purchase order response
 * @param {Object} response - API response from invoice generation endpoint
 * @returns {Object} Formatted invoice generation response
 */
export const generateInvoiceFromPO = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message: response.data.message || 'Invoice generated successfully',
      data: response.data.data || null
    }
  }
  return {
    success: false,
    message: response?.data?.message || 'Failed to generate invoice'
  }
}

/**
 * Process products loading for purchase order response
 * @param {Object} response - API response from products loading endpoint
 * @returns {Object} Formatted products loading response
 */
export const loadProductsForPO = (response) => {
  if (response && response.success === true && response.data) {
    return {
      success: true,
      message: response.message || 'Products loaded successfully',
      data: response.data || []
    }
  }
  return {
    success: false,
    message: response?.message || 'Failed to load products',
    data: []
  }
}

export const supplierListGet = (response) => {
  // Based on Postman collection response format
  if (response && response.data && response.data.success === true) {
    // Map the API response to our frontend model
    const suppliers = Array.isArray(response.data.data) ? response.data.data.map(supplier => ({
      id: supplier.id,
      slack: supplier.slack,
      supplier_code: supplier.supplier_code,
      name: supplier.name,
      email: supplier.email,
      phone: supplier.phone,
      address: supplier.address,
      pincode: supplier.pincode,
      status: supplier.status,
      status_data: supplier.status_data ? {
        id: supplier.status_data.id,
        value: supplier.status_data.value,
        label: supplier.status_data.label
      } : null,
      
    })) : [];
    
    return {
      success: true,
      total: response.data.total || suppliers.length,
      data: suppliers
    };
  }
  return {
    success: false,
    message: response?.data?.message || 'Failed to fetch supplier list',
    data: []
  };
};
